import { type SearchCategory, useGetCategoryPathQuery } from '@kijiji/generated/graphql-types'
import ForSaleIcon from '@kijiji/icons/src/icons/ForSale'
import LocationOutlineIcon from '@kijiji/icons/src/icons/LocationOutline'
import SearchIcon from '@kijiji/icons/src/icons/Search'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { SearchLocationModal } from '@/components/shared/search-location-modal'
import { SearchTip } from '@/components/srp/zero-results/SearchTip'
import {
  IconContainer,
  SearchTipsContainer,
  ZeroResultsPanel,
} from '@/components/srp/zero-results/styled'
import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetSearchKeyword } from '@/hooks/keywords/useGetSearchKeywords'
import { useSearchActions } from '@/hooks/srp/useSearchActions'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/events/search'
import { HeadlineText } from '@/ui/atoms/headline-text'

const ZeroResultsIcon = dynamic(() => import('@kijiji/icons/src/icons/ZeroResults'), {
  ssr: false,
})

export const ZeroResultsActionBox = () => {
  const { t } = useTranslation('srp')
  const { colors } = useTheme()
  const { keyword } = useGetSearchKeyword()
  const { category } = useGetSearchCategory()

  const { refetchResults } = useSearchActions()

  const [parentCategory, setParentCategory] = useState<SearchCategory | null>(null)

  const pageType = getSearchPageType(keyword)

  useGetCategoryPathQuery({
    fetchPolicy: 'cache-first',
    ssr: false,
    skip: !category.parentId,
    variables: { categoryId: category.parentId || ALL_CATEGORIES_ID_NUM },
    onCompleted: ({ category }) => {
      if (!category) {
        setParentCategory(null)
        return
      }

      const parentCategory: SearchCategory = {
        id: category.id,
        localizedName: category.localizedName ?? '',
        parentId: category.parentId,
      }
      setParentCategory(parentCategory)
    },
  })

  const handleCategoryChange = async () => {
    /** If there is no parent category, then there is no category to go up */
    if (!parentCategory) return

    trackEvent({ action: pageType, label: 'SuggestionBoxClick;cta="browse"' })
    await refetchResults({ categoryId: parentCategory.id })
  }

  const handleLocationChange = (openModal: () => void) => {
    trackEvent({ action: pageType, label: 'SuggestionBoxClick;cta="location"' })
    openModal()
  }

  return (
    <ZeroResultsPanel $isFullWidth $alignItems="center" data-testid="zero-results-page">
      <IconContainer>
        <ZeroResultsIcon aria-hidden />
      </IconContainer>

      <HeadlineText size="large" as="h2" color={colors.grey.primary}>
        {t('zero_results.title')}
      </HeadlineText>
      <SearchTipsContainer>
        {parentCategory && (
          <SearchTip
            icon={<ForSaleIcon aria-hidden />}
            text={t('zero_results.broaden_category')}
            actionText={`${t('zero_results.one_category_up')} ${parentCategory.localizedName}`}
            handleClick={handleCategoryChange}
          />
        )}
        <SearchTip
          icon={<SearchIcon aria-hidden />}
          text={t('zero_results.try_different_keyword')}
        />

        <SearchLocationModal>
          {(openModal) => (
            <SearchTip
              icon={<LocationOutlineIcon aria-hidden />}
              text={t('zero_results.expand_search_location')}
              actionText={`${t('zero_results.edit_location')}`}
              handleClick={() => handleLocationChange(openModal)}
            />
          )}
        </SearchLocationModal>
      </SearchTipsContainer>
    </ZeroResultsPanel>
  )
}
